// src/Modals/LoginModal.js
import React, { useState } from "react";
import {
  signInWithEmailAndPassword,
  signInWithPopup,
  GoogleAuthProvider,
  FacebookAuthProvider,
  sendPasswordResetEmail,
} from "firebase/auth";
import { auth, db } from "../firebase";
import { doc, getDoc, setDoc, serverTimestamp } from "firebase/firestore";
import { useNavigate } from "react-router-dom";
import Navbar from "../components/Navbar";

export default function StudentLogin({ onLogin = () => {} }) {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState("");
  const [info, setInfo] = useState("");

  const navigate = useNavigate();

  // Backend URL
  const API_URL = process.env.REACT_APP_API_URL || "https://fmc-backend-c1a5.onrender.com";

  const withUiState = async (fn) => {
    setErr("");
    setInfo("");
    setLoading(true);
    try {
      await fn();
    } catch (e) {
      console.error(e);
      setErr(e?.message || "Something went wrong");
    } finally {
      setLoading(false);
    }
  };

  const fetchOrCreateStudentProfile = async (user) => {
    const { uid, email: userEmail, displayName, photoURL, providerData } = user;
    const docRef = doc(db, "students", uid);
    const snap = await getDoc(docRef);

    let providerId = "custom";
    if (Array.isArray(providerData) && providerData.length > 0) {
      const nonPassword = providerData.find((p) => p.providerId && p.providerId !== "password");
      providerId = nonPassword?.providerId || providerData[0].providerId || "custom";
    } else if (user.providerId) {
      providerId = user.providerId;
    } else if (userEmail) {
      providerId = "password";
    }

    if (snap.exists()) {
      const profile = snap.data();
      await setDoc(docRef, { lastLoginAt: serverTimestamp(), authProvider: providerId }, { merge: true });
      onLogin({ ...profile });
      return profile;
    }

    const fallbackName = (displayName && displayName.trim()) || (userEmail ? userEmail.split("@")[0] : "Student");

    const newProfile = {
      uid,
      name: fallbackName,
      email: userEmail || "",
      phone: "",
      address: "",
      pincode: "",
      createdAt: serverTimestamp(),
      lastLoginAt: serverTimestamp(),
      authProvider: providerId,
    };

    await setDoc(docRef, newProfile, { merge: true });
    onLogin(newProfile);
    return newProfile;
  };

  const handleLogin = (e) => {
    e.preventDefault();
    withUiState(async () => {
      const { user } = await signInWithEmailAndPassword(auth, email, password);

      // Save idToken locally for API calls
      const idToken = await user.getIdToken();
      localStorage.setItem("token", idToken);

      await fetchOrCreateStudentProfile(user);

      // Optionally: inform backend about login (exchange or create server user)
      try {
        const res = await fetch(`${API_URL}/api/signup/login`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${localStorage.getItem("token") || ""}`,
          },
          body: JSON.stringify({ firebaseId: user.uid, email: user.email }),
        });
        const json = await res.json();
        if (json?.success && json?.data?._id) {
          localStorage.setItem("userId", json.data._id);
        }
      } catch (err) {
        // ignore backend login failure for now — frontend auth still works
        console.warn("Backend login/exchange failed:", err);
      }

      navigate("/");
    });
  };

  const handleGoogleLogin = () =>
    withUiState(async () => {
      const provider = new GoogleAuthProvider();
      const result = await signInWithPopup(auth, provider);
      const user = result.user;
      const idToken = await user.getIdToken();
      localStorage.setItem("token", idToken);

      await fetchOrCreateStudentProfile(user);
      navigate("/");
    });

  const handleFacebookLogin = () =>
    withUiState(async () => {
      const provider = new FacebookAuthProvider();
      const result = await signInWithPopup(auth, provider);
      const user = result.user;
      const idToken = await user.getIdToken();
      localStorage.setItem("token", idToken);

      await fetchOrCreateStudentProfile(user);
      navigate("/");
    });

  const handleLinkedInClick = () => {
    setErr("LinkedIn login must be implemented via Firebase Custom Auth.");
  };

  const handleForgotPassword = () =>
    withUiState(async () => {
      if (!email) {
        throw new Error("Enter your email above to receive a reset link.");
      }
      await sendPasswordResetEmail(auth, email);
      setInfo("Password reset email sent. Check your inbox (and spam) for instructions.");
    });

  return (
    <>
      <Navbar />
      <div className="min-h-screen bg-gradient-to-br from-[#2d351f] via-[#353623] to-[#1a1c27] flex items-center justify-center px-4 py-10">
        <div className="w-full max-w-md">
          <div className="backdrop-blur-xl bg-[rgb(36,39,49,0.8)] border border-white/10 text-white rounded-2xl shadow-2xl overflow-hidden">
            {/* Header */}
            <div className="px-8 pt-8 pb-4 text-center">
              <div className="mx-auto h-12 w-12 rounded-xl bg-yellow-500/20 flex items-center justify-center mb-3">
                {/* icon */}
              </div>
              <h2 className="text-3xl font-semibold tracking-tight">Welcome back</h2>
              <p className="text-sm text-white/70 mt-1">Sign in to continue to your student dashboard</p>
            </div>

            {/* Form */}
            <div className="px-8 pb-8">
              {err && <div className="mb-4 rounded-lg border border-red-500/30 bg-red-500/10 text-red-200 px-3 py-2 text-sm">{err}</div>}
              {info && <div className="mb-4 rounded-lg border border-emerald-500/30 bg-emerald-500/10 text-emerald-200 px-3 py-2 text-sm">{info}</div>}

              <form onSubmit={handleLogin} className="space-y-4">
                <div className="space-y-2">
                  <label className="text-sm text-white/80">Email</label>
                  <input
                    type="email"
                    placeholder="name@email.com"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    disabled={loading}
                    className="w-full bg-[#2b2f3a] placeholder-white/40 text-white border border-white/10 rounded-lg px-3 py-2.5"
                    required
                  />
                </div>

                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    <label className="text-sm text-white/80">Password</label>
                    <button type="button" onClick={() => setShowPassword((s) => !s)} className="text-xs text-yellow-500">
                      {showPassword ? "Hide password" : "Show password"}
                    </button>
                  </div>
                  <input
                    type={showPassword ? "text" : "password"}
                    placeholder="••••••••"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    disabled={loading}
                    className="w-full bg-[#2b2f3a] placeholder-white/40 text-white border border-white/10 rounded-lg px-3 py-2.5"
                    required
                  />
                </div>

                <div className="flex items-center justify-between">
                  <div className="text-xs text-white/40">Need help?</div>
                  <button type="button" onClick={handleForgotPassword} className="text-xs text-yellow-500" disabled={loading}>
                    Forgot password?
                  </button>
                </div>

                <button type="submit" disabled={loading} className="w-full bg-yellow-500 text-white py-2.5 rounded-lg">
                  {loading ? "Signing in..." : "Login"}
                </button>
              </form>

              {/* Social & other UI omitted for brevity */}
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
